import * as Api from '@ViewModels';
import { useAccountContext } from '../../../../admin/components/blogs/AccountContext';
import { useDebounceValue } from '../../../../hooks/useDebounceValue';
import { useUserAutocompleteQuery } from '../../../../queries/User/useUserAutocompleteQuery';

const SEARCH_DEBOUNCE_MS = 250;

/**
 * Options for {@link useTeammateSearch}.
 */
export interface IUseTeammateSearchOptions {
	/** The text typed into the picker; nothing is searched until it is non-blank. */
	fragment: string;
	/** Whether the search may run, typically only while the picker is open. */
	enabled: boolean;
	/** Whether deactivated teammates are returned. */
	includeDeactivated?: boolean;
}

/**
 * Type-to-search teammate lookup for the action-item pickers via `user/autocomplete`, debounced and scoped to the
 * impersonated account when an admin is viewing a customer. `hasQuery` is false until something has been typed, in
 * which case `users` is empty and the picker shows a prompt to type instead of results.
 * @param options The typed fragment, whether the search may run, and whether deactivated teammates are included.
 */
export function useTeammateSearch({ fragment, enabled, includeDeactivated = false }: IUseTeammateSearchOptions) {
	const { impersonationContext } = useAccountContext();
	const debouncedFragment = useDebounceValue(fragment.trim(), SEARCH_DEBOUNCE_MS);
	const hasQuery = fragment.trim().length > 0;
	const query = useUserAutocompleteQuery({
		fragment: debouncedFragment,
		includeDeactivated,
		enabled: enabled && hasQuery && debouncedFragment.length > 0,
		impersonationContext: impersonationContext ?? undefined,
	});
	const users: Api.IUser[] = hasQuery ? (query.data?.values ?? []) : [];
	return {
		users,
		hasQuery,
		isLoading: hasQuery && (query.isPending || debouncedFragment !== fragment.trim()),
		isError: hasQuery && query.isError,
	};
}
